import { FakeRepository } from 'src/api/fake_repository';
import {
  DownloadReportResult,
  OrderRepository,
  OrdersQuery,
  OrdersQueryVariables,
} from 'src/api/order_repository';
import {
  ActivityLogQuery,
  CreateOrderInput,
  OrderBaseFieldsFragment,
  OrderStatus,
  Seq,
  SlideImageScannerType,
  SlideMetadataInputType,
  SortBy,
  UploadedFileCategory,
  UploadedFileFieldsFragment,
  UploadedFileStatus,
} from 'src/types';
import { ActivityResourceType } from 'src/types/activtiy_logs';
import { DistributionPlatform } from 'src/types/env';
import { isCustomerSuccessOrSalesUser } from 'src/utils';

/** Fake backend may attach upload connection + LDT-only fields to stored orders. */
type FakeStoredOrder<TOrder extends OrderBaseFieldsFragment> = TOrder & {
  createdByCs?: boolean;
  uploadedFiles?: {
    edges: Array<{
      node: UploadedFileFieldsFragment | null;
    } | null>;
  } | null;
};

/**
 * Base class for the implementation of the Order Repository for a fake backend.
 * Uses session storage to persist data between page reloads.
 */
export abstract class FakeOrderRepository<
  TOrder extends OrderBaseFieldsFragment = OrderBaseFieldsFragment,
>
  extends FakeRepository
  implements OrderRepository<TOrder>
{
  abstract readonly _platform: DistributionPlatform;
  abstract readonly _storageKey: string;
  abstract readonly _initialOrders: TOrder[];

  abstract submit(order: TOrder): Promise<TOrder>;

  /**
   * Compares two dates for sorting. Handles null/undefined values and different date formats.
   * @param dateA First date to compare
   * @param dateB Second date to compare
   * @returns Negative if dateA < dateB, positive if dateA > dateB, 0 if equal
   */
  private compareDates(
    dateA?: Date | string | null,
    dateB?: Date | string | null,
  ): number {
    if (!dateA && !dateB) return 0;
    if (!dateA) return -1;
    if (!dateB) return 1;

    const timeA = new Date(dateA).getTime();
    const timeB = new Date(dateB).getTime();

    if (isNaN(timeA) && isNaN(timeB)) return 0;
    if (isNaN(timeA)) return -1;
    if (isNaN(timeB)) return 1;

    return timeA - timeB;
  }

  /**
   * Compares two order statuses for sorting based on priority.
   * @param statusA First status to compare
   * @param statusB Second status to compare
   * @returns Negative if statusA < statusB, positive if statusA > statusB, 0 if equal
   */
  private compareSatuses(statusA: OrderStatus, statusB: OrderStatus): number {
    if (!statusA) return -1;
    if (!statusB) return 1;

    // Define order status priority (lower number = earlier in workflow)
    const statusPriority = {
      [OrderStatus.Draft]: 1,
      [OrderStatus.LabProcessing]: 2,
      [OrderStatus.AiProcessing]: 3,
      [OrderStatus.AiError]: 4,
      [OrderStatus.ReportPending]: 5,
      [OrderStatus.ReportRejected]: 6,
      [OrderStatus.ReportReady]: 7,
      [OrderStatus.Canceled]: 8,
      // Not used by SAMD
      [OrderStatus.Deleted]: 0,
      [OrderStatus.LabError]: 0,
      [OrderStatus.PathologyFaxFailed]: 0,
      [OrderStatus.PathologyFaxPending]: 0,
      [OrderStatus.QualityControlApproved]: 0,
      [OrderStatus.QualityControlPending]: 0,
      [OrderStatus.ReportFaxed]: 0,
      [OrderStatus.ReportFaxFailed]: 0,
      [OrderStatus.ReportFaxPending]: 0,
      [OrderStatus.ReportSigned]: 0,
      [OrderStatus.SlideImageProcessing]: 0,
      [OrderStatus.SlideImageQualityControlFailed]: 0,
      [OrderStatus.SlideImageQualityControlPending]: 0,
    };

    // Compare based on priority
    return statusPriority[statusA] - statusPriority[statusB];
  }

  protected loadOrders(): TOrder[] {
    const ordersJson = sessionStorage.getItem(this._storageKey);

    if (ordersJson) {
      return JSON.parse(ordersJson);
    }

    return this._initialOrders;
  }

  protected persistOrders(orders: TOrder[]): void {
    sessionStorage.setItem(this._storageKey, JSON.stringify(orders));
  }

  getOrders(ordersQueryVariables: OrdersQueryVariables): Promise<OrdersQuery> {
    let allOrders = this.loadOrders();
    const page = ordersQueryVariables.page;
    const perPage = ordersQueryVariables.perPage;
    const sortBy = ordersQueryVariables.sortBy || SortBy.SubmittedAt;
    const seq = ordersQueryVariables.seq || Seq.Desc;
    const searchText = ordersQueryVariables.searchText || '';
    const statuses = ordersQueryVariables.statuses;

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      allOrders = allOrders.filter(
        (order) =>
          order.externalId?.toString().toLowerCase().includes(searchLower) ||
          order.id?.toLowerCase().includes(searchLower) ||
          order.crossOrgId?.toLowerCase().includes(searchLower),
      );
    }

    // Apply status filter
    if (statuses && statuses.length > 0) {
      allOrders = allOrders.filter((order) => statuses.includes(order.status));
    }

    if (ordersQueryVariables.specimenTypes?.length) {
      allOrders = allOrders.filter((order) =>
        ordersQueryVariables.specimenTypes!.includes(order.specimenType),
      );
    }

    allOrders = [...allOrders].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case SortBy.SubmittedAt:
          comparison = this.compareDates(a.submittedAt, b.submittedAt);
          break;

        case SortBy.Status:
          comparison = this.compareSatuses(a.status, b.status);
          break;

        case SortBy.StatusUpdatedAt:
          comparison = this.compareDates(a.statusUpdatedAt, b.statusUpdatedAt);
          break;
      }

      return seq === Seq.Asc ? comparison : -comparison;
    });

    const totalCount = allOrders.length;
    // Convert from 1-indexed to 0-indexed page (same as real GraphQL backend)
    const pageIndex = Math.max(0, page - 1);
    const startIndex = pageIndex * perPage;
    const endIndex = Math.min(startIndex + perPage, totalCount);
    const paginatedOrders = allOrders.slice(startIndex, endIndex);

    return Promise.resolve({
      orders: {
        page: page,
        totalCount: totalCount,
        items: paginatedOrders,
      },
    });
  }

  getOrderById(
    id: string,
    _options?: { view?: string },
  ): Promise<TOrder | null> {
    if (!id) {
      throw new Error('Order ID is required');
    }

    const orders = this.loadOrders();
    const order = orders.find((order) => order.id === id);

    if (order == null) {
      throw new Error('Not found');
    }

    return Promise.resolve(order);
  }

  create(input: CreateOrderInput): Promise<TOrder> {
    // TODO(joshm): Come back to this once we have better patient persistence.
    const orders = this.loadOrders();
    const id = this.nextOrderId(orders);
    const order = {
      ...input,
      id,
      crossOrgId: id,
      status: OrderStatus.Draft,
      creatorAuth0Id: this.getUserInfo().id,
      createdByCs: isCustomerSuccessOrSalesUser(),
    } as unknown as FakeStoredOrder<TOrder>;
    this.persistOrders([...orders, order]);

    return Promise.resolve(order);
  }

  /**
   * Generates the next base64-encoded order ID in the "Order:N" format that
   * validateOrderIdParam expects (e.g. btoa('Order:3')).  Derives N by
   * scanning all existing order IDs so new orders never collide with seeded
   * ones regardless of how many seeds exist.
   */
  private nextOrderId(existingOrders: TOrder[]): string {
    let max = 0;
    for (const o of existingOrders) {
      try {
        const decoded = atob(o.id);
        const match = decoded.match(/^Order:(\d+)$/);
        if (match) {
          max = Math.max(max, parseInt(match[1], 10));
        }
      } catch {
        // non-base64 ID — skip
      }
    }
    return btoa(`Order:${max + 1}`);
  }

  async update(order: TOrder): Promise<TOrder> {
    const orders = this.loadOrders();
    const existingOrderIndex = orders.findIndex((o) => o.id === order.id);
    let existingOrder = orders[existingOrderIndex];

    if (existingOrderIndex === -1) {
      throw new Error('Order not found');
    }

    existingOrder = { ...existingOrder, ...order };
    orders[existingOrderIndex] = existingOrder;
    this.persistOrders(orders);

    return Promise.resolve(existingOrder);
  }

  cancel(order: TOrder): Promise<TOrder> {
    order.status = OrderStatus.Canceled;
    return this.update(order);
  }

  async createMultipartUpload(
    filename: string,
    category: UploadedFileCategory,
    orderId: string,
    _countParts: number,
    _signal?: AbortSignal,
    _slideMetadata?: SlideMetadataInputType,
    _scannerType?: SlideImageScannerType,
  ): Promise<string> {
    const fileId = Math.random().toString(36).substring(7);

    // Add in-progress upload to the order's uploadedFiles
    const orders = this.loadOrders();
    const orderIndex = orders.findIndex((o) => o.id === orderId);

    if (orderIndex !== -1) {
      const order = orders[orderIndex] as FakeStoredOrder<TOrder>;

      // Remove any existing IN-PROGRESS uploads for the same category if it is a single file category
      // This allows concurrent uploads of different files in the same category
      // Keep completed uploads in the backend data
      if (order.uploadedFiles?.edges) {
        order.uploadedFiles.edges = order.uploadedFiles.edges.filter(
          (edge) =>
            (edge?.node?.category &&
              [
                UploadedFileCategory.SlideImage,
                UploadedFileCategory.TestRequisitionForm,
              ].includes(edge.node.category)) ||
            edge?.node?.status !== UploadedFileStatus.InProgress,
        );
      }

      // Initialize uploadedFiles if it doesn't exist
      if (!order.uploadedFiles) {
        order.uploadedFiles = {
          edges: [],
        };
      }

      // Add in-progress upload
      order.uploadedFiles.edges.push({
        node: {
          id: fileId,
          filename,
          category: category,
          status: UploadedFileStatus.InProgress,
          progress: 0,
          createdAt: new Date().toISOString(),
          uploaderAuth0Id: null,
        },
      });

      orders[orderIndex] = order;
      this.persistOrders(orders);
    }

    return fileId;
  }

  async completeUpload(fileId: string): Promise<void> {
    // Find the order that contains this upload and update its status
    const orders = this.loadOrders();
    let updated = false;

    for (let i = 0; i < orders.length; i++) {
      const order = orders[i] as FakeStoredOrder<TOrder>;

      if (order.uploadedFiles?.edges) {
        // Find and update the upload status
        const uploadEdge = order.uploadedFiles.edges.find(
          (edge) => edge?.node?.id === fileId,
        );

        if (uploadEdge?.node) {
          uploadEdge.node.status = UploadedFileStatus.Complete;
          uploadEdge.node.progress = 100;
          updated = true;

          if (uploadEdge?.node?.category === UploadedFileCategory.SlideImage) {
            order.status = OrderStatus.AiProcessing;
          }
          break;
        }
      }
    }

    if (updated) {
      this.persistOrders(orders);
    }
  }

  async cancelUpload(fileId: string): Promise<void> {
    // Find the order that contains this upload and remove it
    const orders = this.loadOrders();
    let updated = false;

    for (let i = 0; i < orders.length; i++) {
      const order = orders[i] as FakeStoredOrder<TOrder>;

      if (order.uploadedFiles?.edges) {
        const originalLength = order.uploadedFiles.edges.length;

        // Remove the canceled upload
        order.uploadedFiles.edges = order.uploadedFiles.edges.filter(
          (edge) => edge?.node?.id !== fileId,
        );

        if (order.uploadedFiles.edges.length < originalLength) {
          updated = true;
          break;
        }
      }
    }

    if (updated) {
      this.persistOrders(orders);
    }
  }

  // This method does not exist for all platforms, so we implement it as a no-op here.
  // For the platforms that do use it, it will be overridden in the concrete implementation.
  async deleteFile(_: string): Promise<void> {}

  async deleteOrder(id: string): Promise<void> {
    const orders = this.loadOrders();
    const filtered = orders.filter((order) => order.id !== id);
    this.persistOrders(filtered);
  }

  async downloadTRR(
    _orderId: string,
    _signed: boolean,
    _isDeidentified: boolean,
  ): Promise<DownloadReportResult> {
    // For fake implementation, return a mock presigned URL.
    return Promise.resolve({ success: true, url: '' });
  }

  async getActivityLogs(orderId: string): Promise<ActivityLogQuery> {
    // For fake implementation, return mock activity logs
    return Promise.resolve({
      activityLog: {
        edges: [
          {
            node: {
              id: '1',
              action: 'create',
              createdAt: '2023-10-01T12:00:00Z',
              details: JSON.stringify({
                updated_fields: ['externalId', 'patientAge', 'psa'],
                values: {
                  externalId: 'p_patient_id_1',
                  patientAge: 65,
                  psa: 1.0,
                },
              }),
              principalId: 'user123',
              principalName: 'john.doe@example.com',
              resourceId: orderId,
              resourceType: ActivityResourceType.ORDER,
              updatedAt: '2023-10-01T12:00:00Z',
            },
          },
          {
            node: {
              id: '2',
              action: 'update',
              createdAt: '2023-10-01T12:30:00Z',
              details: JSON.stringify({
                updated_fields: ['status'],
                values: {
                  status: 'DRAFT',
                },
              }),
              principalId: 'user123',
              principalName: 'john.doe@example.com',
              resourceId: orderId,
              resourceType: ActivityResourceType.ORDER,
              updatedAt: '2023-10-01T12:30:00Z',
            },
          },
          {
            node: {
              id: '3',
              action: 'submit',
              createdAt: '2023-10-01T13:00:00Z',
              details: JSON.stringify({
                updated_fields: ['status'],
                values: {
                  status: 'LAB_PROCESSING',
                },
              }),
              principalId: 'user456',
              principalName: 'jane.smith@example.com',
              resourceId: orderId,
              resourceType: ActivityResourceType.ORDER,
              updatedAt: '2023-10-01T13:00:00Z',
            },
          },
        ],
      },
    });
  }
}
